import React, { useState, useEffect } from 'react';
import {
  Card,
  Descriptions,
  Button,
  Typography,
  Space,
  Switch,
  Modal,
  Form,
  Input,
  App,
  Tag,
  Divider,
  Alert,
  Avatar
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  SafetyOutlined,
  KeyOutlined,
  LockOutlined
} from '@ant-design/icons';
import PageHeader from './PageHeader';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import authService from '../services/authService';
import TwoFactorSetup from './TwoFactorSetup';

const { Title, Text } = Typography;

const UserProfile = () => {
  const { message, modal } = App.useApp();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
  const [disabling2FA, setDisabling2FA] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    setLoading(true);
    try {
      const response = await authService.getProfile();
      if (response.success) {
        setUser(response.data);
        setTwoFactorEnabled(response.data.twoFactorEnabled || false);
      } else {
        message.error(response.message || 'Failed to load profile');
      }
    } catch (error) {
      message.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAToggle = (checked) => {
    if (checked) {
      // Enable 2FA - show setup modal
      setShowTwoFactorSetup(true);
    } else {
      // Disable 2FA - show confirmation modal
      setShowDisable2FAModal(true);
    }
  };

  const handleDisable2FA = async (values) => {
    setDisabling2FA(true);
    try {
      const result = await authService.disable2FA(values.password);
      if (result.success) {
        message.success('2FA disabled successfully');
        setTwoFactorEnabled(false);
        setShowDisable2FAModal(false);
        form.resetFields();
        // Update user info in localStorage
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          currentUser.twoFactorEnabled = false;
          localStorage.setItem('userInfo', JSON.stringify(currentUser));
        }
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Failed to disable 2FA');
    } finally {
      setDisabling2FA(false);
    }
  };

  const handle2FASetupComplete = () => {
    setShowTwoFactorSetup(false);
    setTwoFactorEnabled(true);
    message.success('2FA enabled successfully');
    // Update user info in localStorage
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      currentUser.twoFactorEnabled = true;
      localStorage.setItem('userInfo', JSON.stringify(currentUser));
    }
  };

  const handle2FASetupCancel = () => {
    setShowTwoFactorSetup(false);
  };

  const handleChangePassword = async (values) => {
    setChangingPassword(true);
    try {
      const result = await authService.changePassword(values.currentPassword, values.newPassword);
      if (result.success) {
        message.success('Password changed successfully');
        setShowChangePasswordModal(false);
        form.resetFields();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <PageHeader
          title="User Profile"
          subtitle="Manage your account settings"
          breadcrumbs={['Dashboard', 'Profile']}
        />
        <Card loading />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <PageHeader
        title="My Profile"
        subtitle="Manage your account settings and security"
        breadcrumbs={['Dashboard', 'Profile']}
      />

      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        {/* Profile Information Card */}
        <Card
          style={{ flex: '1 1 400px', minWidth: '300px' }}
          bodyStyle={{ padding: '32px' }}
        >
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <Avatar 
              size={100} 
              style={{ 
                backgroundColor: '#2E86AB',
                fontSize: '36px',
                marginBottom: '16px'
              }}
            >
              {user?.name?.charAt(0)}{user?.surname?.charAt(0)}
            </Avatar>
            <Title level={3} style={{ marginBottom: '4px', marginTop: '16px' }}>
              {user?.name} {user?.surname}
            </Title>
            <span 
              style={{ 
                fontSize: '13px', 
                padding: '3px 10px',
                fontWeight: '500',
                color: '#2E86AB',
                backgroundColor: 'rgba(46, 134, 171, 0.1)',
                borderRadius: '4px',
                display: 'inline-block',
                marginTop: '8px'
              }}
            >
              {user?.userType === 'regular' ? 'Admin' : (user?.userType?.charAt(0).toUpperCase() + user?.userType?.slice(1))}
            </span>
          </div>

          <Divider style={{ margin: '24px 0' }} />

          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                EMAIL ADDRESS
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MailOutlined style={{ color: '#2E86AB' }} />
                <Text strong style={{ fontSize: '15px' }}>{user?.email || '-'}</Text>
              </div>
            </div>

            <div>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                PHONE NUMBER
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <PhoneOutlined style={{ color: '#2E86AB' }} />
                <Text strong style={{ fontSize: '15px' }}>
                  {user?.phoneNumber?.full || 
                   (user?.phoneNumber?.countryCode && user?.phoneNumber?.number 
                     ? `${user.phoneNumber.countryCode}${user.phoneNumber.number}` 
                     : '-')}
                </Text>
              </div>
            </div>

            <div>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                GROUP
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <UserOutlined style={{ color: '#2E86AB' }} />
                <Text strong style={{ fontSize: '15px' }}>{user?.group?.groupName || 'No Group'}</Text>
              </div>
            </div>

            <div>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                STATUS
              </Text>
              <Tag 
                color={user?.status === 'active' ? 'success' : 'default'}
                style={{ fontSize: '13px', padding: '2px 10px' }}
              >
                {(user?.status || 'active').charAt(0).toUpperCase() + (user?.status || 'active').slice(1)}
              </Tag>
            </div>
          </Space>
        </Card>

        {/* Security Settings Card - Only for regular users (admins) */}
        {user?.userType === 'regular' && (
          <Card
            style={{ flex: '1 1 400px', minWidth: '300px' }}
            bodyStyle={{ padding: '32px' }}
          >
            <div style={{ marginBottom: '24px' }}>
              <Title level={4} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <SafetyOutlined style={{ color: '#2E86AB' }} />
                Security Settings
              </Title>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Protect your account with additional security features
              </Text>
            </div>

            <Divider style={{ margin: '24px 0' }} />

            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <div>
                    <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: '4px' }}>
                      Two-Factor Authentication
                    </Text>
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                      Add extra security to your account
                    </Text>
                  </div>
                  <Switch
                    checked={twoFactorEnabled}
                    onChange={handle2FAToggle}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                  />
                </div>
                
                {twoFactorEnabled && (
                  <Alert
                    message="2FA is active"
                    type="success"
                    showIcon
                    style={{ marginTop: '12px', fontSize: '13px' }}
                  />
                )}
              </div>

              <Divider style={{ margin: '16px 0' }} />

              <div>
                <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: '8px' }}>
                  Password
                </Text>
                <Button
                  icon={<KeyOutlined />}
                  onClick={() => setShowChangePasswordModal(true)}
                  block
                  size="large"
                  style={{ marginTop: '8px' }}
                >
                  Change Password
                </Button>
              </div>
            </Space>
          </Card>
        )}
      </div>

      {/* 2FA Setup Modal */}
      <TwoFactorSetup
        visible={showTwoFactorSetup}
        onComplete={handle2FASetupComplete}
        onCancel={handle2FASetupCancel}
      />

      {/* Disable 2FA Modal */}
      <Modal
        title="Disable Two-Factor Authentication"
        open={showDisable2FAModal}
        onCancel={() => {
          setShowDisable2FAModal(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Alert
          message="Warning"
          description="Disabling 2FA will make your account less secure. You'll only need your password to sign in."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />
        
        <Form
          form={form}
          layout="vertical"
          onFinish={handleDisable2FA}
        >
          <Form.Item
            name="password"
            label="Enter your password to confirm"
            rules={[
              { required: true, message: 'Please enter your password' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Your password"
              size="large"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setShowDisable2FAModal(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button
                type="primary"
                danger
                htmlType="submit"
                loading={disabling2FA}
              >
                Disable 2FA
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title="Change Password"
        open={showChangePasswordModal}
        onCancel={() => {
          setShowChangePasswordModal(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Alert
          message="Security Tip"
          description="Choose a strong password with at least 6 characters, including letters, numbers, and special characters."
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />
        <Form
          form={form}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[
              { required: true, message: 'Please enter your current password' }
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Enter current password"
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Enter new password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Confirm new password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setShowChangePasswordModal(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={changingPassword}
                icon={<KeyOutlined />}
              >
                Change Password
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserProfile;
